import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import ExtensionOffOutlinedIcon from "@mui/icons-material/ExtensionOffOutlined";

/**
 * Route → module map for the module on/off toggles. The backend already
 * blocks disabled modules' APIs (403 MODULE_DISABLED); this guard keeps users
 * from landing on a broken-looking page via bookmark / autocomplete / back
 * button. Prefixes are matched case-insensitively (React Router v6 routes are
 * case-insensitive) as exact path or "<prefix>/...".
 */
const ROUTE_MODULES = [
  { prefix: "/recruitment", key: "recruitment" },
  { prefix: "/referrals", key: "recruitment" },
  { prefix: "/approvals/recruitment", key: "recruitment" },
  { prefix: "/onboarding", key: "onboarding" },
  { prefix: "/offboarding", key: "offboarding" },
  { prefix: "/performance", key: "performance" },
  { prefix: "/review-cycles", key: "performance" },
  { prefix: "/reviews", key: "performance" },
  { prefix: "/my-reviews", key: "performance" },
  { prefix: "/nine-box", key: "performance" },
  { prefix: "/pips", key: "performance" },
  { prefix: "/team-self-assessments", key: "performance" },
  { prefix: "/selfassessmentform", key: "performance" },
  { prefix: "/my-self-assessments", key: "performance" },
  { prefix: "/self-assessments", key: "performance" },
  { prefix: "/goals", key: "goals" },
  { prefix: "/check-ins", key: "goals" },
  { prefix: "/managerfeedbackform", key: "feedbackForms" },
  { prefix: "/manager-feedback", key: "feedbackForms" },
  { prefix: "/employee-feedback", key: "feedbackForms" },
  { prefix: "/projects", key: "projects" },
  { prefix: "/whiteboard", key: "whiteboard" },
  { prefix: "/docs", key: "docs" },
  { prefix: "/chatboard", key: "chat" },
  { prefix: "/groupchatboard", key: "chat" },
  { prefix: "/videoupload", key: "video" },
  { prefix: "/helpdesk", key: "helpdesk" },
  { prefix: "/kb", key: "helpdesk" },
  { prefix: "/kudos", key: "engagement" },
  { prefix: "/surveys", key: "engagement" },
  { prefix: "/training", key: "training" },
  { prefix: "/learning", key: "training" },
  { prefix: "/announcements", key: "announcements" },
  { prefix: "/policies", key: "policies" },
  { prefix: "/overtime", key: "overtime" },
  { prefix: "/overtimerequest", key: "overtime" },
  { prefix: "/managerovertimerequests", key: "overtime" },
  { prefix: "/travel", key: "travel" },
  { prefix: "/reimbursementform", key: "reimbursement" },
  { prefix: "/myreimbursements", key: "reimbursement" },
  { prefix: "/my-reimbursements", key: "reimbursement" },
  { prefix: "/reimbursements", key: "reimbursement" },
  { prefix: "/submit-reimbursement", key: "reimbursement" },
  { prefix: "/reimbursementlist", key: "reimbursement" },
  { prefix: "/reimbursementreport", key: "reimbursement" },
  { prefix: "/expensedashboard", key: "reimbursement" },
  { prefix: "/viewalltimesheets", key: "timesheet" },
  { prefix: "/submit-timesheet", key: "timesheet" },
  { prefix: "/timesheet", key: "timesheet" },
  { prefix: "/timesheetacess", key: "timesheet" },
  { prefix: "/timesheetlist", key: "timesheet" },
  { prefix: "/myinventories", key: "inventory" },
  { prefix: "/inventorylist", key: "inventory" },
  { prefix: "/inventoryform", key: "inventory" },
  { prefix: "/inventorysearch", key: "inventory" },
  { prefix: "/inventorydetail", key: "inventory" },
  { prefix: "/assigninventorytoemployee", key: "inventory" },
  { prefix: "/payroll/revisions", key: "salaryRevisions" },
  { prefix: "/payroll/statutory", key: "statutory" },
  { prefix: "/workflows", key: "workflows" },
  { prefix: "/integrations", key: "integrationsStore" },
  { prefix: "/reports/dei", key: "analytics" },
  { prefix: "/reports/attrition-risk", key: "analytics" },
];

// Paths that must NEVER be module-gated even though a prefix above matches
// (API keys management is not part of the Integrations Store module).
const EXCLUDES = ["/integrations/api-keys"];

export function moduleKeyForPath(pathname) {
  const p = String(pathname || "").toLowerCase();
  if (EXCLUDES.some((e) => p === e || p.startsWith(`${e}/`))) return null;
  const hit = ROUTE_MODULES.find((r) => p === r.prefix || p.startsWith(`${r.prefix}/`));
  return hit ? hit.key : null;
}

export function ModuleDisabledPage() {
  const navigate = useNavigate();
  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
      <ExtensionOffOutlinedIcon sx={{ fontSize: 64, color: "text.secondary" }} />
      <Typography variant="h5" fontWeight={600}>This module is turned off</Typography>
      <Typography color="text.secondary" textAlign="center" maxWidth={420}>
        Your admin has disabled this module for the organization, so its pages and data
        are unavailable. If you think you need it, ask an admin to re-enable it in Module Settings.
      </Typography>
      <Button variant="contained" onClick={() => navigate("/EmployeeDashboard")}>Go to Dashboard</Button>
    </Box>
  );
}
